import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import DetailCard, { ICardDetail } from '@/components/common/DetailCard';
import Logs from '@/components/common/Logs';
import ViewDocumentModal from '@/components/common/ViewDocumentModal';
import Layout from '@/components/Layout/Layout';
import FullPageLoader from '@/components/loader/FullPageLoader';
import BackButton from '@/components/ui/BackButton';
import { IAsset, IAssetDetailResponse } from '@/interfaces/assets.model';
import { IDocument } from '@/interfaces/user.model';
import { handleAssetStatus } from '@/utility/CommonFunctions';
import { showToastNotification } from '@/utility/helper';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const AssetDetail: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isViewModal, setIsViewModal] = useState<boolean>(false);
  const { id } = useParams();
  const [assetDetails, setAssetDetails] = useState<IAsset>();
  const [selectedDocument, setSelectedDocument] =
    useState<Partial<IDocument> | null>(null);

  const getAsset = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: IAssetDetailResponse = await callApi(
        API_URL + API_ENDPOINT.getAsset + `?assetId=${id}`,
        {
          method: 'GET',
        },
      );
      if (response.status) {
        setAssetDetails(response.data);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getAsset();
    }
  }, [id, getAsset]);

  const toggle = () => {
    setIsViewModal(!isViewModal);
  };

  const handleDocumentClick = (doc: IDocument | string) => {
    const url = typeof doc == 'string' ? doc : doc?.url;
    const docUrl = {
      url,
    };

    setSelectedDocument(docUrl);
    setIsViewModal(true);
  };

  const cardDetails: ICardDetail[] = [
    { label: 'Name', value: assetDetails?.name },
    {
      label: 'Serial Number',
      value: assetDetails?.serialNo,
    },
    { label: 'Price', value: assetDetails?.price },
    {
      label: 'Purchase Date',
      value: moment(assetDetails?.purchaseDate).format('DD/MM/YYYY'),
    },
    { label: 'Category', value: assetDetails?.assetCategory?.name },
    { label: 'Description', value: assetDetails?.description },
    { label: 'Status', value: handleAssetStatus(Number(assetDetails?.status)) },
  ];

  return (
    <Layout>
      {isLoading ? (
        <FullPageLoader />
      ) : (
        <>
          <div className="content container-fluid">
            <BackButton label={'Asset Detail'} isDetail={true} />
            <DetailCard cardDetails={cardDetails} />

            <div className="row">
              <DetailCard
                title="Documents"
                documents={assetDetails?.documents}
                handleDocumentClick={handleDocumentClick}
              />
            </div>
            <Logs refreshLogs={true} type="asset" id={id ?? ''} />
          </div>
          <ViewDocumentModal
            isOpen={isViewModal}
            toggle={toggle}
            selectedDocument={selectedDocument}
          />
        </>
      )}
    </Layout>
  );
};
export default AssetDetail;
